import React, { useCallback, useMemo } from "react";

import {
  QBackLink,
  QBodyLayout,
  QButtonGroup,
  QHeader,
  QMenuButton,
  QMenuItem,
  QStack,
  QTabs,
  QText,
  QTitle,
} from "@qualio/ui-components";
import { useLocation, useNavigate } from "react-router";
import { useTranslation } from "react-i18next";

import { RequirementModal } from "../components/CreateDesignElement/RequirementModal";
import { RiskModal } from "../components/CreateDesignElement/RiskModal";
import { TestCaseModal } from "../components/CreateDesignElement/TestCaseModal";
import { useCurrentProduct } from "../hooks/useCurrentProduct";
import { useRequirementModal } from "../hooks/useRequirementModal";
import { useRiskModal } from "../hooks/useRiskModal";
import { useTestCaseModal } from "../hooks/useTestCaseModal";
import { ManageProduct } from "../components/ManageProduct/ManageProduct";

export const ListLayout: React.FC = ({ children }) => {
  const { t } = useTranslation();
  const product = useCurrentProduct();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const { openModal: openRequirementModal } = useRequirementModal();
  const { openModal: openTestCaseModal } = useTestCaseModal();
  const { openModal: openRiskModal } = useRiskModal();

  const TABS: [string, string, string][] = [
    ["requirement", "requirement", t("layout.tabs.requirements")],
    ["testCase", "test-case", t("layout.tabs.tests")],
    ["risk", "risk", t("layout.tabs.risks")],
    ["traceability", "traceability", t("layout.tabs.traceability")],
    ["document", "document", t("layout.tabs.documents")],
    ["reviews", "change-control", t("layout.tabs.reviews")],
  ];

  const onItemChange = useCallback(
    (index: number) => {
      const item = TABS.at(index);
      if (!item) {
        return;
      }

      navigate(`/product/${product.id}/${item[1]}`);
    },
    [navigate, product],
  );

  const createReview = useCallback(() => {
    navigate("../change-control/create");
  }, [navigate]);

  const activeTabId = useMemo(() => {
    const item = TABS.find(([, routePath]) => pathname.includes(routePath));

    if (!item) {
      return "requirement";
    }

    return item[0];
  }, [pathname]);

  const onClick = useCallback(
    (type: "requirement" | "testCase") => () => {
      if (type === "requirement") {
        openRequirementModal();
      } else {
        openTestCaseModal();
      }
    },
    [openRequirementModal, openTestCaseModal],
  );

  return (
    <QBodyLayout.Default>
      <QHeader showDivider={false}>
        <QBackLink href="../../">{t("layout.navigation.back")}</QBackLink>
        <QTitle>
          <QText data-cy="list-product-name" as="span">
            {product.name}
          </QText>
        </QTitle>
        <QButtonGroup>
          <QMenuButton
            buttonLabel={t("layout.createMenu.label")}
            variant="solid"
            data-cy="create-new"
          >
            <QMenuItem
              onClick={onClick("requirement")}
              data-cy="create-requirement"
            >
              {t("layout.createMenu.items.requirement")}
            </QMenuItem>
            <QMenuItem onClick={onClick("testCase")} data-cy="create-test-case">
              {t("layout.createMenu.items.testCase")}
            </QMenuItem>
            <QMenuItem onClick={openRiskModal} data-cy="create-risk">
              {t("layout.createMenu.items.risk")}
            </QMenuItem>
            <QMenuItem onClick={createReview} data-cy="create-review">
              {t("layout.createMenu.items.review")}
            </QMenuItem>
          </QMenuButton>
          <ManageProduct />
        </QButtonGroup>
      </QHeader>
      <QStack gap={4}>
        <QTabs
          activeTabId={activeTabId}
          onChange={onItemChange}
          tabs={TABS.map(([id, , title]) => ({ id, title }))}
        />
        {children}
      </QStack>
      <RequirementModal />
      <TestCaseModal />
      <RiskModal />
    </QBodyLayout.Default>
  );
};
